import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomModal from "../CustomModal";
import CustomButton from "../CustomButton";
import {
  defaultInviteEmailBody,
  defaultInviteEmailSubject,
  defaultInviteSmsBody,
} from "../../utils/growNetwork";

const InviteConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  count,
  inviterName,
  loading,
  emailCount = 0,
  smsCount = 0,
}) => {
  const hasEmail = emailCount > 0;
  const hasSms = smsCount > 0;
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sms, setSms] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSubject(defaultInviteEmailSubject(inviterName));
    setBody(defaultInviteEmailBody(inviterName));
    setSms(defaultInviteSmsBody(inviterName));
  }, [isOpen, inviterName]);

  const handleSend = () => {
    onConfirm({
      custom_subject: subject.trim(),
      custom_body: body.trim(),
      custom_sms: sms.trim(),
    });
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Invites"
      maxWidth="520px"
    >
      <p>
        You are about to send <strong>{count}</strong> invite
        {count === 1 ? "" : "s"} from Workbrook
        {hasEmail && hasSms
          ? ` (${emailCount} email, ${smsCount} SMS)`
          : hasSms
            ? " via SMS"
            : " via email"}
        .
      </p>
      <p className="small text-muted">
        This is the final step. Edit the message below, then send only if you
        are ready to notify these contacts.
      </p>
      {hasEmail && (
        <div className="gn-message-editor">
          <strong>Email message</strong>
          <label className="gn-message-label" htmlFor="gn-invite-subject">
            Subject
          </label>
          <input
            id="gn-invite-subject"
            type="text"
            className="form-control gn-contacts-search"
            maxLength={150}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <label className="gn-message-label" htmlFor="gn-invite-body">
            Body
          </label>
          <textarea
            id="gn-invite-body"
            className="form-control gn-contacts-search"
            rows={4}
            maxLength={1500}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <p className="small text-muted mb-0 mt-2">
            Recipients still get a Join Workbrook button with your invite link.
          </p>
        </div>
      )}
      {hasSms && (
        <div className="gn-message-editor">
          <strong>SMS message</strong>
          <label className="gn-message-label" htmlFor="gn-invite-sms">
            Text
          </label>
          <textarea
            id="gn-invite-sms"
            className="form-control gn-contacts-search"
            rows={3}
            maxLength={240}
            value={sms}
            onChange={(e) => setSms(e.target.value)}
          />
          <p className="small text-muted mb-0 mt-2">
            Use <code>[invite_link]</code> where the signup link should appear.
            If you remove it, the link is added at the end.
          </p>
        </div>
      )}
      <div className="d-flex justify-content-end">
        <CustomButton
          text="Cancel"
          backgroundColor="#fff"
          color="#64748B"
          borderColor="#CBD5E1"
          handleClick={onClose}
          additionalClass="mr-2"
        />
        <CustomButton
          text={loading ? "Sending..." : `Send ${count} Invite${count === 1 ? "" : "s"}`}
          backgroundColor="#3A6ED4"
          color="#fff"
          handleClick={handleSend}
          disabled={loading || (hasEmail && !subject.trim()) || (hasSms && !sms.trim())}
        />
      </div>
    </CustomModal>
  );
};

InviteConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  inviterName: PropTypes.string,
  loading: PropTypes.bool,
  emailCount: PropTypes.number,
  smsCount: PropTypes.number,
};

export default InviteConfirmModal;
